import React from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import {
  CostsAndFeesContextProvider,
  useCostsAndFeesContext,
} from 'app/shared/modules/hdp/CostsAndFees/context/CostsAndFeesProvider';
import { hasFeesInSection } from 'app/shared/modules/hdp/CostsAndFees/utils';
import { processApiTextContent } from 'app/shared/utils/totalMonthlyPriceUtils';
import Card from '../../../ui/Card';
import { SpecialOfferBanner } from './SpecialOfferBanner';
import { CostSummaryCard } from './CostSummaryCard';
import { FeeBreakdownCard } from './FeeBreakdownCard';
import { LeaseTermsInline } from './LeaseTermsInline';
import { UnitPicker } from './UnitPicker';
import * as styles from './costAndFeesModule.styles';

const SpokeContentInner = () => {
  const { calculatedCostsAndFees } = useCostsAndFeesContext();

  if (!calculatedCostsAndFees) return null;

  const { fees, costs } = calculatedCostsAndFees;

  return (
    <>
      <UnitPicker />
      <CostSummaryCard />

      {fees?.monthly && hasFeesInSection(fees.monthly) && (
        <FeeBreakdownCard
          title="Monthly rent & fees"
          feeSection={fees.monthly}
          totalDisplayName={costs?.monthly?.displayName}
          totalAmount={costs?.monthly?.amount}
        />
      )}

      {fees?.moveIn && hasFeesInSection(fees.moveIn) && (
        <FeeBreakdownCard
          title="One-time fees"
          feeSection={fees.moveIn}
          totalDisplayName={costs?.moveIn?.displayName}
          totalAmount={costs?.moveIn?.amount}
        />
      )}
    </>
  );
};

const SpokeDisclaimers = () => {
  const { calculatedCostsAndFees } = useCostsAndFeesContext();

  const pmDisclaimer = calculatedCostsAndFees?.disclaimer;
  const processed = processApiTextContent(pmDisclaimer);

  return (
    <div className={styles.disclaimerContainer}>
      {processed.content && (
        <p className={styles.disclaimer}>
          {processed.isHtml ? (
            <span dangerouslySetInnerHTML={{ __html: processed.content }} />
          ) : (
            <span style={{ whiteSpace: 'pre-wrap' }}>{processed.content}</span>
          )}
        </p>
      )}
    </div>
  );
};

export const CostAndFeesSpokeContent = () => {
  const listing = useSelector((state: RootReduxState) => state.currentListingDetails?.currentListing);

  if (!listing) return null;

  return (
    <section>
      <CostsAndFeesContextProvider floorPlans={listing.floorplans as never}>
        <Card variant="default" size="md" title="Estimate your total">
          <div className={styles.subcardsContainer}>
            <SpecialOfferBanner />
            <SpokeContentInner />
            <LeaseTermsInline />
          </div>
        </Card>
        <SpokeDisclaimers />
      </CostsAndFeesContextProvider>
    </section>
  );
};
